import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { User, UserRole } from '../types';

/**
 * Date and time, not just the date.
 *
 * A consent record answers "when did this person agree", and to the
 * minute is the useful resolution — two accounts registered the same
 * afternoon are indistinguishable otherwise. Rendered in the viewer's
 * own locale and zone, which for this programme is the zone the
 * applicant was actually sitting in.
 */
function formatDateTime(value?: Date | string) {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const NOT_RECORDED = (
  <span style={{ color: 'var(--text-secondary)' }} title="No acceptance was recorded for this account.">
    Not recorded
  </span>
);

/**
 * Who has accepted the User Agreement, and when.
 *
 * Reads the user list rather than a dedicated endpoint: the acceptance
 * is two columns on the account, so there is nothing to join and nothing
 * a second endpoint would add beyond another thing to keep in step.
 *
 * Only applicants are listed. Staff accounts are created by a Super
 * Admin through User Management, which never collects consent — an
 * administrator cannot agree on someone else's behalf — so including
 * them would fill the table with rows that read "Not recorded" and mean
 * nothing by it.
 */
const AgreementAcceptances = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);

  useEffect(() => {
    apiService
      .getUsers()
      .then((all) => setUsers(all.filter((u) => u.role === UserRole.APPLICANT)))
      .catch((err: any) => setError(err.message || 'Failed to load acceptances'))
      .finally(() => setLoading(false));
  }, []);

  const accepted = useMemo(() => users.filter((u) => u.privacyConsentAt || u.termsConsentAt), [users]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => {
        if (missingOnly && (u.privacyConsentAt || u.termsConsentAt)) return false;
        if (!q) return true;
        return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      })
      // Most recent acceptance first: the useful reading of this table is
      // "who agreed lately", and accounts with no record sort to the end
      // rather than the top.
      .sort((a, b) => {
        const at = a.privacyConsentAt ? new Date(a.privacyConsentAt).getTime() : -1;
        const bt = b.privacyConsentAt ? new Date(b.privacyConsentAt).getTime() : -1;
        return bt - at;
      });
  }, [users, query, missingOnly]);

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header">
        <h3>User Agreement Acceptances</h3>
      </div>

      <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        Every student account that accepted the Data Privacy Policy and the Terms and Conditions at
        registration, and when. One tick box covers both documents, so the two times normally match.
      </p>

      {error && (
        <div className="alert-error" style={{ padding: '1rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
              <label htmlFor="acceptanceSearch">Search</label>
              <input
                id="acceptanceSearch"
                placeholder="Name or email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0' }}>
                <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />
                <span style={{ fontSize: '0.9rem' }}>Show only accounts with no record</span>
              </label>
            </div>
          </div>

          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{accepted.length.toLocaleString()}</strong> of{' '}
            {users.length.toLocaleString()} student account{users.length === 1 ? '' : 's'} have a recorded
            acceptance.
          </p>

          {/* Said once, here, because the gap is otherwise invisible: a
              row reading "Not recorded" is not an account that refused,
              it is one that registered before the record existed. */}
          {accepted.length < users.length && (
            <div
              style={{
                padding: '0.85rem 1rem',
                marginBottom: '1rem',
                background: 'var(--warning-bg)',
                border: '1px solid var(--warning-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--warning-text)',
                fontSize: '0.85rem'
              }}
            >
              Accounts showing "Not recorded" registered before acceptance was captured. They were never asked,
              so nothing was recorded — this is not a refusal, and it is deliberately not backfilled.
            </div>
          )}

          {rows.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>No accounts match this filter.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Data Privacy Policy</th>
                    <th>Terms and Conditions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u, index) => (
                    <tr key={u.id}>
                      <td>{index + 1}</td>
                      <td>
                        {u.firstName} {u.lastName}
                      </td>
                      <td>{u.email}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* The version hash sits in the tooltip: it is
                            what proves which wording was agreed to, but
                            it is unreadable and nobody scans a column of
                            hashes. */}
                        <span
                          title={
                            u.privacyConsentVersion ? `Version ${u.privacyConsentVersion.slice(0, 12)}` : undefined
                          }
                        >
                          {formatDateTime(u.privacyConsentAt) ?? NOT_RECORDED}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span
                          title={u.termsConsentVersion ? `Version ${u.termsConsentVersion.slice(0, 12)}` : undefined}
                        >
                          {formatDateTime(u.termsConsentAt) ?? NOT_RECORDED}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgreementAcceptances;
